import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE } from '@shared/api';
import { CodeBlock, CopyButton, Icon, Tabs } from './ui';
import type { Lang } from '../snippets';

type Method = 'GET' | 'POST' | 'DELETE' | 'PATCH';

const ORIGIN = API_BASE.replace(/\/$/, '');

function PathText({ path }: { path: string }) {
  const parts = path.split(/(\{[^}]+\})/g);
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith('{') ? (
          <i key={i}>{part}</i>
        ) : (
          <Fragment key={i}>{part}</Fragment>
        ),
      )}
    </>
  );
}

function MethodBadge({ method }: { method: Method }) {
  return <span className={`docs-method ${method.toLowerCase()}`}>{method}</span>;
}

export function Endpoint({ method, path, note }: { method: Method; path: string; note?: ReactNode }) {
  return (
    <div className="docs-endpoint">
      <MethodBadge method={method} />
      <code className="docs-path">
        <PathText path={path} />
      </code>
      {note && <span className="docs-endpoint-note">{note}</span>}
      <CopyButton value={`${ORIGIN}${path}`} label="Salin URL" className="btn-ghost" />
    </div>
  );
}

export function EndpointList({ items }: { items: Array<{ method: Method; path: string; note: ReactNode }> }) {
  return (
    <div className="docs-routes">
      {items.map((item) => (
        <div key={`${item.method} ${item.path}`} className="docs-route">
          <MethodBadge method={item.method} />
          <code className="docs-path">
            <PathText path={item.path} />
          </code>
          <span className="docs-route-note">{item.note}</span>
        </div>
      ))}
    </div>
  );
}

export interface Param {
  name: string;
  type: string;
  required?: boolean;
  desc: ReactNode;
}

export function ParamTable({ params }: { params: Param[] }) {
  return (
    <div className="docs-params">
      {params.map((param) => (
        <div key={param.name} className="docs-param">
          <div className="docs-param-key">
            <div className="docs-param-names">
              {param.name.split(' / ').map((name) => (
                <code key={name}>{name}</code>
              ))}
            </div>
            <div className="docs-param-meta">
              <span>{param.type}</span>
              {param.required && <span className="docs-req">wajib</span>}
            </div>
          </div>
          <div className="docs-param-desc">{param.desc}</div>
        </div>
      ))}
    </div>
  );
}

/** Folded extra content. */
export function More({ label, count, children }: { label: string; count?: number; children: ReactNode }) {
  return (
    <details className="docs-more">
      <summary>
        <Icon name="right" size={15} className="docs-more-chevron" />
        <span>{label}</span>
        {count !== undefined && <span className="docs-more-count">{count}</span>}
      </summary>
      <div className="docs-more-body">{children}</div>
    </details>
  );
}

export function DocTable({
  head,
  rows,
  className,
}: {
  head: ReactNode[];
  rows: Array<{ key: string; cells: ReactNode[] }>;
  className?: string;
}) {
  return (
    <div className="docs-box">
      <div className="table-wrap">
        <table className={className}>
          <thead>
            <tr>
              {head.map((cell, i) => (
                <th key={i}>{cell}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                {row.cells.map((cell, i) => (
                  <td key={i}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function Callout({ title, tone = 'info', children }: { title?: string; tone?: 'info' | 'warn'; children: ReactNode }) {
  return (
    <div className={`docs-callout ${tone}`}>
      <Icon name={tone === 'warn' ? 'warn' : 'info'} size={17} className="docs-callout-icon" />
      <div className="docs-callout-body">
        {title && <div className="docs-callout-title">{title}</div>}
        {children}
      </div>
    </div>
  );
}

export function LangCode({
  code,
  lang,
  langs,
  onLang,
}: {
  code: string;
  lang: Lang;
  langs: ReadonlyArray<{ id: Lang; label: string }>;
  onLang: (lang: Lang) => void;
}) {
  return <CodeBlock code={code} tabs={<Tabs tight tabs={langs} value={lang} onChange={onLang} />} />;
}

export function DocHead({ group, title, children }: { group: string; title: string; children?: ReactNode }) {
  return (
    <header className="docs-head">
      <nav className="docs-crumb" aria-label="Lokasi halaman">
        <Link to="/docs">Dokumentasi</Link>
        <Icon name="right" size={13} />
        <span>{group}</span>
      </nav>
      <h1>{title}</h1>
      {children}
    </header>
  );
}

export function DocSection({ id, title, lead, children }: { id: string; title: string; lead?: ReactNode; children: ReactNode }) {
  return (
    <section id={id} className="docs-part" aria-labelledby={`${id}-title`}>
      <h2 id={`${id}-title`}>{title}</h2>
      {lead && <p className="docs-lead">{lead}</p>}
      {children}
    </section>
  );
}
